import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface DocumentDownloadConsentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept: () => void;
  documentName: string;
}

export const DocumentDownloadConsent: React.FC<DocumentDownloadConsentProps> = ({
  open,
  onOpenChange,
  onAccept,
  documentName
}) => {
  const handleAccept = () => {
    onAccept();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl font-semibold">
            Document Download License Agreement
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-muted-foreground">
            Please review and accept the terms below to proceed with downloading "{documentName}"
          </AlertDialogDescription>
        </AlertDialogHeader>

        <ScrollArea className="max-h-96 pr-4">
          <div className="space-y-4 text-sm">
            <section>
              <h3 className="font-semibold text-foreground mb-2">License Grant and Restrictions</h3>
              <p className="text-muted-foreground leading-relaxed">
                By downloading this document, you acknowledge that the content is licensed to you for personal or professional use only. 
                This license is non-transferable and does not grant you ownership rights to the intellectual property contained within.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-2">Security and Confidentiality</h3>
              <p className="text-muted-foreground leading-relaxed">
                You agree to maintain the confidentiality and security of the downloaded document. You are responsible for:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-muted-foreground">
                <li>Storing the document in a secure location with appropriate access controls</li>
                <li>Preventing unauthorized access to or distribution of the document</li>
                <li>Implementing reasonable security measures to protect the document from theft, loss, or unauthorized disclosure</li>
                <li>Notifying us immediately if you become aware of any unauthorized access or security breach</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-2">Prohibited Activities</h3>
              <p className="text-muted-foreground leading-relaxed">
                You expressly agree NOT to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-muted-foreground">
                <li>Share, distribute, or provide access to the document to any third party</li>
                <li>Make copies of the document except for personal backup purposes</li>
                <li>Modify, adapt, or create derivative works based on the document</li>
                <li>Use the document for any illegal or unauthorized purpose</li>
                <li>Remove or alter any copyright notices, watermarks, or proprietary markings</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-2">Legal Responsibility</h3>
              <p className="text-muted-foreground leading-relaxed">
                You acknowledge that any breach of these terms may result in irreparable harm to the content owner and agree to be liable for any damages 
                resulting from unauthorized use or distribution. You also agree that the content owner may seek immediate injunctive relief to prevent such breaches.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-2">Acceptance</h3>
              <p className="text-muted-foreground leading-relaxed">
                By clicking "Accept and Download", you confirm that you have read, understood, and agree to be bound by all terms and conditions outlined above.
              </p>
            </section>
          </div>
        </ScrollArea>

        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel>
            Cancel Download
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleAccept}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Accept and Download
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};